import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2 } from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editorMode, setEditorMode] = useState('rich'); // 'rich' or 'html'
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    featured_image: '',
    category: '',
    author: 'Editor',
    reading_time: 5,
    is_featured: false,
    tags: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [postsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/posts`),
        axios.get(`${API}/categories`),
      ]);
      setPosts(postsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingPost) {
        await axios.put(`${API}/posts/${editingPost.id}`, formData);
        toast.success('Post updated successfully!');
      } else {
        await axios.post(`${API}/posts`, formData);
        toast.success('Post created successfully!');
      }
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save post. Please try again.');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${API}/posts/${postId}`);
      toast.success('Post deleted successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete post.');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt,
      featured_image: post.featured_image,
      category: post.category,
      author: post.author,
      reading_time: post.reading_time,
      is_featured: post.is_featured,
      tags: post.tags,
    });
    setIsCreating(true);
  };

  const resetForm = () => {
    setIsCreating(false);
    setEditingPost(null);
    setEditorMode('rich');
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      featured_image: '',
      category: '',
      author: 'Editor',
      reading_time: 5,
      is_featured: false,
      tags: [],
    });
  };

  return (
    <div className="min-h-screen bg-secondary">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 md:px-12 py-16">
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-4xl font-black" data-testid="admin-dashboard-title">Admin Dashboard</h1>
          {!isCreating && (
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors"
              data-testid="create-post-btn"
            >
              <Plus className="w-5 h-5" />
              <span>Create Post</span>
            </button>
          )}
        </div>

        {isCreating ? (
          <div className="bg-white p-8 border border-border" data-testid="post-form">
            <h2 className="text-2xl font-bold mb-6">{editingPost ? 'Edit Post' : 'Create New Post'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold mb-2">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-border focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="post-title-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold mb-2">Excerpt</label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    required
                    rows={3}
                    className="w-full px-4 py-3 border border-border focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="post-excerpt-input"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-bold">Content</label>
                    <div className="flex items-center space-x-2 border border-border">
                      <button
                        type="button"
                        onClick={() => setEditorMode('rich')}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                          editorMode === 'rich' 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-white text-foreground hover:bg-secondary'
                        }`}
                        data-testid="rich-editor-toggle"
                      >
                        📝 Rich Text
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditorMode('html')}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                          editorMode === 'html' 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-white text-foreground hover:bg-secondary'
                        }`}
                        data-testid="html-editor-toggle"
                      >
                        💻 HTML
                      </button>
                    </div>
                  </div>
                  
                  {editorMode === 'rich' ? (
                    <RichTextEditor
                      value={formData.content}
                      onChange={(content) => setFormData({ ...formData, content })}
                      data-testid="post-content-editor"
                    />
                  ) : (
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      required
                      rows={20}
                      className="w-full px-4 py-3 border border-border focus:outline-none focus:ring-2 focus:ring-ring font-mono text-sm"
                      placeholder="<p>Paste or write your HTML here...</p>"
                      data-testid="post-html-editor"
                      style={{ fontFamily: 'monospace' }}
                    />
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    {editorMode === 'rich' 
                      ? '💡 Use toolbar buttons to format text' 
                      : '💡 Write or paste HTML code directly. Make sure your HTML is valid!'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-bold mb-2">Featured Image URL</label>
                  <input
                    type="url"
                    value={formData.featured_image}
                    onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-border focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="post-image-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold mb-2">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-border focus:outline-none focus:ring-2 focus:ring-ring"
                      data-testid="post-category-select"
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.slug}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold mb-2">Reading Time (minutes)</label>
                    <input
                      type="number"
                      value={formData.reading_time}
                      onChange={(e) => setFormData({ ...formData, reading_time: parseInt(e.target.value) })}
                      required
                      min="1"
                      className="w-full px-4 py-3 border border-border focus:outline-none focus:ring-2 focus:ring-ring"
                      data-testid="post-reading-time-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold mb-2">Author</label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-border focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="post-author-input"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="w-5 h-5 mr-3"
                    data-testid="post-featured-checkbox"
                  />
                  <label className="text-sm font-bold">Featured Post</label>
                </div>
              </div>

              <div className="flex space-x-4 mt-6">
                <button
                  type="submit"
                  className="px-6 py-3 bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors"
                  data-testid="post-save-btn"
                >
                  {editingPost ? 'Update Post' : 'Create Post'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 border border-foreground bg-transparent text-foreground font-bold hover:bg-secondary transition-colors"
                  data-testid="post-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="bg-white border border-border" data-testid="posts-list">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border">
                  <tr>
                    <th className="text-left py-4 px-6 font-bold">Title</th>
                    <th className="text-left py-4 px-6 font-bold">Category</th>
                    <th className="text-left py-4 px-6 font-bold">Views</th>
                    <th className="text-left py-4 px-6 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <tr key={post.id} className="border-b border-border/40" data-testid={`post-row-${post.id}`}>
                      <td className="py-4 px-6">
                        <div className="font-bold">{post.title}</div>
                        <div className="text-sm text-muted-foreground">{post.slug}</div>
                      </td>
                      <td className="py-4 px-6">{post.category}</td>
                      <td className="py-4 px-6">{post.views}</td>
                      <td className="py-4 px-6">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(post)}
                            className="p-2 hover:bg-secondary transition-colors"
                            data-testid={`edit-post-${post.id}`}
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="p-2 hover:bg-destructive/10 text-destructive transition-colors"
                            data-testid={`delete-post-${post.id}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
